import axios, { type AxiosInstance, type AxiosError } from 'axios'
import { ElMessage } from 'element-plus'
import { appDateStamp } from '@/config/timezone'
import type {
  Post,
  PostDetail,
  Stats,
  ValidationStats,
  HealthStatus,
  Source,
  SourceListResponse,
  SourceLogsResponse,
  SourceStatusResponse,
  ValidationPayload,
  ValidationResponse,
  ReportDownloadPayload,
  ReportPreview,
  FetchPostsParams,
  ManualPostCreate,
  ManualPostCreateResponse,
  SummarizeResponse,
  ManualPostUpdate,
  ChartStats,
} from '@/types'

export const API_BASE = (import.meta as any).env?.VITE_API_URL || '/api'

const client: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

export function getCsrfToken(): string | null {
  const match = document.cookie.match(new RegExp('(^| )csrf_token=([^;]+)'))
  return match ? decodeURIComponent(match[2]) : null
}

client.interceptors.request.use(
  (config) => {
    if (config.method && ['post', 'patch', 'delete', 'put'].includes(config.method.toLowerCase())) {
      const csrf = getCsrfToken()
      if (csrf) {
        config.headers = config.headers || {}
        config.headers['X-CSRF-Token'] = csrf
      }
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor — handle common errors
client.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname
      if (currentPath !== '/login') {
        window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`
      }
    } else if (error.response?.status === 403) {
      ElMessage.error('Недостаточно прав')
    } else if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      ElMessage.error('Превышено время ожидания. Попробуйте снова.')
    } else if (error.response) {
      const status = error.response.status
      if (status === 404) {
        ElMessage.error('Ресурс не найден.')
      } else if (status === 500) {
        ElMessage.error('Ошибка сервера. Попробуйте позже.')
      } else {
        ElMessage.error(`Ошибка ${status}: ${error.message}`)
      }
    } else if (error.request) {
      ElMessage.error('Не удалось подключиться к API. Сервер запущен?')
    } else {
      ElMessage.error(`Ошибка запроса: ${error.message}`)
    }
    return Promise.reject(error)
  }
)

// Stats
export async function fetchStats(dateFrom?: string, dateTo?: string): Promise<Stats> {
  const { data } = await client.get<Stats>('/stats', {
    params: { date_from: dateFrom, date_to: dateTo },
  })
  return data
}

// Posts
export async function fetchFlaggedPosts(params: FetchPostsParams = {}): Promise<Post[]> {
  const { data } = await client.get<Post[]>('/posts/flagged', { params })
  return data
}

export async function searchPosts(q: string, limit = 50, offset = 0): Promise<Post[]> {
  const { data } = await client.get<Post[]>('/posts/search', {
    params: { q, limit, offset },
  })
  return data
}

export async function fetchRecentPosts(limit = 20, hours = 24): Promise<Post[]> {
  const { data } = await client.get<Post[]>('/posts/recent', {
    params: { limit, hours },
  })
  return data
}

export async function fetchPostDetail(postId: number): Promise<PostDetail> {
  const { data } = await client.get<PostDetail>(`/posts/${postId}`)
  return data
}

// Validations
export async function submitValidation(payload: ValidationPayload): Promise<ValidationResponse> {
  const { data } = await client.post<ValidationResponse>('/validations', payload)
  return data
}

export async function fetchValidationStats(dateFrom?: string, dateTo?: string): Promise<ValidationStats> {
  const { data } = await client.get<ValidationStats>('/validations/stats', {
    params: { date_from: dateFrom, date_to: dateTo },
  })
  return data
}

// Sources (v2)
export async function fetchSources(
  source_type?: string,
  is_active?: boolean,
  limit = 50,
  offset = 0,
): Promise<SourceListResponse> {
  const { data } = await client.get<SourceListResponse>('/v2/sources', {
    params: { source_type, is_active, limit, offset },
  })
  return data
}

export async function fetchSourceDetail(sourceId: number): Promise<Source> {
  const { data } = await client.get<Source>(`/v2/sources/${sourceId}`)
  return data
}

export async function fetchSourceLogs(
  sourceId: number,
  limit = 50,
  offset = 0,
): Promise<SourceLogsResponse> {
  const { data } = await client.get<SourceLogsResponse>(`/v2/sources/${sourceId}/logs`, {
    params: { limit, offset },
  })
  return data
}

export async function createSource(payload: {
  source_type: string
  external_id: string
  display_name: string
  is_active?: boolean
  config?: Record<string, any>
}): Promise<Source> {
  const response = await client.post<Source>('/v2/sources', payload, {
    validateStatus: (status) => status < 400 || status === 409,
  })

  if (response.status === 409) {
    const error = new Error(`Request failed with status code ${response.status}`) as Error & {
      response: typeof response
    }
    error.response = response
    throw error
  }

  return response.data
}

export async function updateSource(
  sourceId: number,
  payload: Partial<Pick<Source, 'display_name' | 'is_active' | 'config'>>,
): Promise<Source> {
  const { data } = await client.patch<Source>(`/v2/sources/${sourceId}`, payload)
  return data
}

export async function fetchSourceStatus(): Promise<SourceStatusResponse> {
  const { data } = await client.get<SourceStatusResponse>('/v2/sources/status')
  return data
}

// Health
export async function fetchReady(): Promise<HealthStatus> {
  const { data } = await client.get<HealthStatus>('/ready')
  return data
}

export async function fetchHealthz(): Promise<{ status: 'ok' }> {
  const { data } = await client.get<{ status: 'ok' }>('/healthz')
  return data
}

// Графики
export async function fetchChartStats(dateFrom?: string, dateTo?: string): Promise<ChartStats> {
  const { data } = await client.get<ChartStats>('/stats/charts', {
    params: { date_from: dateFrom, date_to: dateTo },
  })
  return data
}

// Отчеты
export async function previewDailyReport(reportDate?: string, includePostIds: number[] = []): Promise<ReportPreview> {
  const response = await client.get<ReportPreview>('/reports/daily/preview', {
    params: {
      ...(reportDate ? { report_date: reportDate } : {}),
      include_post_ids: includePostIds,
    },
    paramsSerializer: { indexes: null },
    validateStatus: (status) => status < 400 || status === 422,
  })

  if (response.status === 422) {
    const error = new Error(`Request failed with status code ${response.status}`) as Error & {
      response: typeof response
    }
    error.response = response
    throw error
  }

  return response.data
}

export async function downloadDailyReport(payload: ReportDownloadPayload): Promise<void> {
  const reportDate = payload.report_date
  const response = await client.post('/reports/daily/download', payload, {
    responseType: 'blob',
    validateStatus: (status) => status < 400 || status === 422,
  })

  if (response.status === 422) {
    const data = await parseBlobJson(response.data)
    const error = new Error(`Request failed with status code ${response.status}`) as Error & {
      response: typeof response
    }
    error.response = { ...response, data }
    throw error
  }

  const blob = new Blob([response.data])
  const blobUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = blobUrl

  const filename = reportDate
    ? `report_${reportDate}.docx`
    : `report_${appDateStamp()}.docx`
  link.setAttribute('download', filename)

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(blobUrl)
}

async function parseBlobJson(data: unknown): Promise<unknown> {
  if (!(data instanceof Blob)) return data

  const text = await data.text()
  if (!text) return data

  try {
    return JSON.parse(text)
  } catch {
    return data
  }
}

// Manual post creation
export async function createManualPost(payload: ManualPostCreate): Promise<ManualPostCreateResponse> {
  const response = await client.post<ManualPostCreateResponse>('/posts', payload, {
    validateStatus: (status) => status < 400 || status === 409,
  })

  if (response.status === 409) {
    const error = new Error(`Request failed with status code ${response.status}`) as Error & {
      response: typeof response
    }
    error.response = response
    throw error
  }

  return response.data
}

export async function updateManualPost(postId: number, payload: ManualPostUpdate): Promise<ManualPostCreateResponse> {
  const { data } = await client.patch<ManualPostCreateResponse>(`/posts/${postId}`, payload)
  return data
}

export async function summarizeText(text: string): Promise<SummarizeResponse> {
  const { data } = await client.post<SummarizeResponse>('/posts/summarize', { text })
  return data
}

export default client
